import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './lib/queryClient'
import { FrappeProvider } from 'frappe-react-sdk'
import { ClerkProvider, SignedIn, SignedOut } from '@clerk/clerk-react'

import { apiBase } from './lib/api/apiBase'
import { router } from './app/routes'
import { ThemeProvider } from './components/ThemeProvider'
import { SessionProvider } from './components/SessionProvider'
import { ActiveEnquiryProvider } from './components/ActiveEnquiryProvider'
import { ClerkIdentityBridge } from './components/ClerkIdentityBridge'
import { ClerkSignInScreen } from './components/ClerkSignInScreen'
import { FrappeSessionGate } from './components/FrappeSessionGate'
import { FrappeLoginGate } from './components/FrappeLoginGate'
import { ToastProvider } from './components/Toast'
import {
  CLERK_NOT_CONFIGURED_WARNING,
  resolveAuthProvider, resolveClerkAuthMode,
  resolveEnableSocket,
  resolveSessionBridgeEnabled,
} from './lib/auth'
import './styles/global.css'

/**
 * Clerk gates access to BOSUN — it is a sign-in wall and an identity source, not end-to-end
 * auth on its own. Every `/api/method/quoflow.*` call is still authorised solely by Frappe's
 * own `sid` session cookie; `FrappeSessionGate` below is what obtains that cookie, exchanging
 * the Clerk session for a Frappe one via `lib/api/frappeSession.ts` before the app tree (and
 * its `FrappeProvider` socket) ever mounts. That exchange establishes *identity*, not
 * *authority* — Frappe still enforces every role gate server-side against the Frappe user, and
 * a Clerk session carries no privilege of its own. See `lib/auth.ts`'s header comment for the
 * full boundary, and docs/bosun-go-live-checklist.md's E1/F1/F2 for what's still open.
 *
 * `resolveClerkAuthMode` is the pure (and unit-tested — see `lib/auth.test.ts`) decision of
 * which tree below to mount. No key configured degrades to exactly today's stub behaviour
 * (`SessionProvider` picks a pretend user/role, nothing gates the router) so a developer
 * without a Clerk key is never blocked — this is also how that fallback path gets verified
 * here, since this repo has no component-test infrastructure to exercise it another way.
 */
const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
const clerkAuthMode = resolveClerkAuthMode(clerkPublishableKey)
/**
 * `VITE_BOSUN_AUTH=frappe` — the instance signs people in with their own Frappe username and
 * password, served by the Frappe site itself (Akhil, 2026-09-24). No Clerk tree at all, no token
 * anywhere: the `sid` cookie from `/api/method/login` authorises every call, and each one runs as
 * the person who made it. Anything else keeps today's Clerk wall — see `resolveAuthProvider`.
 */
const authProvider = resolveAuthProvider(import.meta.env.VITE_BOSUN_AUTH, clerkPublishableKey)

/**
 * `VITE_BOSUN_SESSION_BRIDGE=0` — an explicit, documented opt-out from mounting
 * `FrappeSessionGate`, for the one combination it breaks: `BOSUN_LIVE_URL` (live Frappe) plus a
 * Clerk key, where `auth.session_from_clerk` doesn't exist yet on the live server and the gate
 * hard-blocks the app instead of the proxy-injected API token already authorising everything, as
 * it did before this bridge existed. See `resolveSessionBridgeEnabled`'s own comment in
 * `lib/auth.ts` for why this must stay explicit rather than becoming automatic, and
 * `docs/bosun-go-live-checklist.md`'s "Running it today" section for how to set it.
 */
const sessionBridgeEnabled = resolveSessionBridgeEnabled(import.meta.env.VITE_BOSUN_SESSION_BRIDGE)

if (clerkAuthMode === 'stub') {
  // Single, clear warning — not an error, since running keyless is the expected default in dev.
  console.warn(CLERK_NOT_CONFIGURED_WARNING)
}

/**
 * `FrappeProvider` with no `url` uses the window's origin — the same-origin,
 * session-cookie setup the contract specifies (§1). In dev, Vite proxies `/api` to the
 * Express mock, so nothing here changes between mock and real Frappe.
 *
 * `enableSocket` defaults to `VITE_LIVE_BACKEND` — true whenever `vite.config.ts` has
 * `BOSUN_LIVE_URL` configured, since that's exactly when mail.new / sq.received / extract.done /
 * enquiry.status are real events the socket can receive (confirmed via `mail.get_mailbox_info`).
 * Against the mock there is nothing listening on `/socket.io`, so the default is off there too.
 * `VITE_ENABLE_SOCKET=true|false` in `.env` overrides the default either way.
 */
const enableSocket = resolveEnableSocket(
  import.meta.env.VITE_ENABLE_SOCKET,
  import.meta.env.VITE_LIVE_BACKEND,
)

/**
 * frappe-react-sdk's socket does NOT follow `fetch` to the window origin the way `url`-less
 * `FrappeProvider` reads/writes do: with no explicit `url`, it connects to
 * `window.location.hostname` on port `socketPort ?? "9000"` whenever `window.location.port` is
 * set (true for this dev server) — bypassing Vite's `/api`-style proxy entirely. Passing this dev
 * server's own port makes the socket target itself instead, so it hits the `/socket.io` proxy
 * rule in vite.config.ts and reaches `BOSUN_LIVE_URL` from there. Must match vite.config.ts's
 * `DEV_SERVER_PORT`. In a production build `window.location.port` is empty, so this is ignored
 * and the socket connects same-origin — the SDK's own doc comment on `socketPort` says as much:
 * "Only meant for local development. Set to undefined on production."
 */
const DEV_SERVER_PORT = '5173'

// The client itself lives in `lib/queryClient.ts` — see its header for why it had to move out
// of this file. This file still owns the provider below.

// Unchanged from before Clerk existed — same provider stack the header comments above describe.
// `SessionProvider` reads the Clerk identity itself (via `ClerkIdentityBridge`'s context, which
// safely returns `null` with no `ClerkIdentityBridge` above it), so nothing here needs to thread
// identity through as a prop.
const app = (
  <FrappeProvider url={apiBase() || undefined} enableSocket={enableSocket} socketPort={import.meta.env.DEV ? DEV_SERVER_PORT : undefined}>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <SessionProvider>
          <ActiveEnquiryProvider>
            <ToastProvider>
              <RouterProvider router={router} />
            </ToastProvider>
          </ActiveEnquiryProvider>
        </SessionProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </FrappeProvider>
)

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {authProvider === 'frappe' ? (
      /* Password sign-in against the site's own session. OUTSIDE `app` for the same reason the
         Clerk gate is: `FrappeProvider` opens the socket at mount and it authenticates from the
         cookie this gate is what obtains. */
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <FrappeLoginGate>{app}</FrappeLoginGate>
        </ThemeProvider>
      </QueryClientProvider>
    ) : clerkAuthMode === 'clerk' && clerkPublishableKey ? (
      <ClerkProvider publishableKey={clerkPublishableKey} afterSignOutUrl="/">
        <ClerkIdentityBridge>
          <SignedIn>
            {/* OUTSIDE `app`, deliberately: `app`'s outermost provider is FrappeProvider, which
                opens the socket at mount, and the socket authenticates from the `sid` cookie the
                gate is what obtains. Inside, the socket would connect unauthenticated.
                `sessionBridgeEnabled` is the documented `VITE_BOSUN_SESSION_BRIDGE=0` escape
                hatch above — skipping the gate here is the ONLY effect it has; nothing else in
                this tree changes. */}
            {sessionBridgeEnabled ? (
              <FrappeSessionGate queryClient={queryClient}>{app}</FrappeSessionGate>
            ) : (
              app
            )}
          </SignedIn>
          <SignedOut>
            <ClerkSignInScreen />
          </SignedOut>
        </ClerkIdentityBridge>
      </ClerkProvider>
    ) : (
      // Keyless dev: no gate, no token source, no session exchange — every retry path checks
      // `hasClerkTokenSource()` and is inert. Exactly today's stub behaviour.
      app
    )}
  </StrictMode>,
)
